import React, { useCallback, useEffect, useState } from 'react';

import type { ConfigureSSOBody, EnableDomainVerificationBody, UUID } from '@aline/common';
import { RedirectURIOptionalSchema } from '@aline/common';
import { routes } from '@aline/routes';
import { useApolloClient } from '@apollo/client/react';
import {
  Autocomplete,
  Card,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { capitalCase } from 'change-case';
import copy from 'copy-to-clipboard';
import { AlineInput } from 'src/AlineInput';
import {
  AlineButton,
  AlineTooltip,
  ConfirmationDialog,
  CurrentUserOrgAvatar,
  NotEnoughSeatsWarning,
  ToggleSwitch,
  UploadOrgAvatarPicker,
} from 'src/components';
import { AlineAutocompleteInput } from 'src/components/AlineAutocompleteInput';
import { useAccessGuard } from 'src/components/auth';
import { CopyIcon } from 'src/components/icons/CopyIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { useCurrentUserQuery, useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import {
  FileStackDomContainer,
  useDispatchNotification,
  useDispatchSuccessSnack,
  useLogAndDispatchErrorSnack,
} from 'src/helpers';
import { formatDateForDisplay } from 'src/helpers/dates';
import { postFetch } from 'src/helpers/fetcher';
import { useRepositoryMeta } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import { useCreateOrUpdateOrg } from './hooks';
import { formatUsageNumber } from '../usage/usage-data';

const useStyles = makeStyles()(theme => ({
  betaChip: {
    '& .MuiChip-label': {
      padding: theme.spacing(0, 0.75),
    },
    fontSize: 10,
    fontWeight: 600,
    height: 18,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: 600,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  inputLabel: {
    ...theme.appTypography.fieldLabel,
    marginBottom: theme.spacing(1),
  },
  orgIdInput: {
    '& input': {
      color: theme.palette.text.secondary,
      cursor: 'default',
      fontSize: 12,
    },
  },
  page: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    padding: theme.spacing(0, 2, 2, 2),
    width: '100%',
  },
  roleSelect: {
    minWidth: 120,
  },
  signatureDetectionBackfillButton: {
    flexShrink: 0,
  },
  // Inset under the Signature Detection switch, tinted so it reads as part of that setting.
  signatureDetectionBackfillPanel: {
    alignItems: 'center',
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    margin: theme.spacing(0, 2, 2, 2),
    padding: theme.spacing(1.5, 2),
  },
  signingReminderSelect: {
    minWidth: 180,
  },
  ssoCard: {
    marginTop: theme.spacing(2),
  },
  ssoContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(2),
  },
  ssoTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  statusRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0, 2, 2, 2),
  },
  // Tooltips need an element that takes a ref, which the icon itself does not.
  titlePulse: {
    display: 'flex',
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  toggleContainer: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
  },
  uploadAvatar: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  uploadText: {
    color: theme.appColors.primary,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 700,
  },
}));

interface EditOrgValues {
  memberEmails: string[];
  orgName: string;
}

const ORG_DEFAULT_ROLE_OPTIONS = [
  Apollo.OrgRole.Viewer,
  Apollo.OrgRole.Basic,
  Apollo.OrgRole.Full,
  Apollo.OrgRole.Admin,
];

interface SigningReminderOption {
  label: string;
  value: number | null;
}

const SIGNING_REMINDER_NONE_OPTION: SigningReminderOption = { label: 'None', value: null };

const SIGNING_REMINDER_OPTIONS: SigningReminderOption[] = [
  SIGNING_REMINDER_NONE_OPTION,
  ...Array.from({ length: 14 }, (_, index): SigningReminderOption => {
    const days = index + 1;
    return { label: `Every ${days} ${days === 1 ? 'Day' : 'Days'}`, value: days };
  }),
];

export function EditOrgPage() {
  const { classes } = useStyles();
  const { updateOrg } = useCreateOrUpdateOrg();
  const [isFocused, setIsFocused] = useState(false);
  const { currentUser, handleSSOClick, loading: ssoLoading } = useManageSSO();
  const { handleDirectorySyncClick, loading: dirSyncLoading } = useManageDirectorySync();
  const { handleDomainVerificationClick, loading: domainVerificationLoading } =
    useManageDomainVerification();
  const canUpdateOrg = currentUser?.role === Apollo.OrgRole.Admin;
  const handleManageSSOClick = useAccessGuard({
    callback: handleSSOClick,
    minRole: Apollo.OrgRole.Admin,
  });
  const handleManageDirectorySyncClick = useAccessGuard({
    callback: handleDirectorySyncClick,
    minRole: Apollo.OrgRole.Admin,
  });
  const handleManageDomainVerificationClick = useAccessGuard({
    callback: handleDomainVerificationClick,
    minRole: Apollo.OrgRole.Admin,
  });
  const [orgName, setOrgName] = useState(currentUser?.org?.name ?? '');
  const [openUploadAvatarPicker, setOpenUploadAvatarPicker] = useState<boolean>(false);
  const dispatchCopySnack = useDispatchSuccessSnack({ message: 'Org ID copied to clipboard.' });

  useEffect(() => {
    if (!isFocused) setOrgName(currentUser?.org?.name ?? '');
  }, [currentUser?.org?.name, isFocused]);

  const debouncedSave = useDebouncedCallback((name: string) => {
    if (!currentUser?.org?.id) return;
    updateOrg(currentUser.org.id, {
      dispatchSnack: false,
      newMemberEmails: [],
      props: { name },
    });
  }, 500);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setOrgName(value);
      debouncedSave(value);
    },
    [debouncedSave]
  );

  const handleCopyOrgId = useCallback(() => {
    copy(currentUser?.org?.id ?? '');
    dispatchCopySnack();
  }, [currentUser?.org?.id, dispatchCopySnack]);

  const handleOrgDefaultRoleChange = useCallback(
    (orgDefaultRole: Apollo.OrgRole) => {
      if (!currentUser?.org?.id) return;
      updateOrg(currentUser.org.id, {
        newMemberEmails: [],
        props: { orgDefaultRole },
      });
    },
    [currentUser?.org?.id, updateOrg]
  );

  const handleSigningReminderChange = useCallback(
    (selected: SigningReminderOption | null) => {
      if (!currentUser?.org?.id) return;
      updateOrg(currentUser.org.id, {
        newMemberEmails: [],
        props: { signingReminderDays: selected?.value ?? null },
      });
    },
    [currentUser?.org?.id, updateOrg]
  );

  const signingReminderValue =
    SIGNING_REMINDER_OPTIONS.find(
      option => option.value === (currentUser?.org?.signingReminderDays ?? null)
    ) ?? SIGNING_REMINDER_NONE_OPTION;

  return (
    <div className={classes.page}>
      <div className={classes.container}>
        <NotEnoughSeatsWarning selectedRole={Apollo.OrgRole.Full} />
        <div className={classes.uploadAvatar}>
          <CurrentUserOrgAvatar />
          <Typography
            className={classes.uploadText}
            onClick={() => setOpenUploadAvatarPicker(true)}
          >
            Change avatar
          </Typography>
        </div>
        <div className={classes.form}>
          <div>
            <InputLabel className={classes.inputLabel}>Organization Name</InputLabel>
            <AlineInput
              value={orgName}
              onBlur={() => setIsFocused(false)}
              onChange={handleNameChange}
              onFocus={() => setIsFocused(true)}
            />
          </div>
          <div>
            <InputLabel className={classes.inputLabel}>Organization ID</InputLabel>
            <AlineInput
              readOnly
              className={classes.orgIdInput}
              endAdornment={
                <AlineTooltip arrow placement="top" title="Copy Organization ID">
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleCopyOrgId}>
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                </AlineTooltip>
              }
              value={currentUser?.org?.id ?? ''}
            />
          </div>
          <Card className={classes.ssoCard}>
            <div className={classes.ssoContent}>
              <div className={classes.ssoTextContainer}>
                <Typography variant="subtitle1">Default Member Role</Typography>
                <Typography color="textSecondary" variant="body2">
                  The role new members get when they join your Aline organization.
                </Typography>
              </div>
              <FormControl className={classes.roleSelect} size="small">
                <Select<Apollo.OrgRole>
                  disabled={!canUpdateOrg}
                  value={currentUser?.org?.orgDefaultRole ?? Apollo.OrgRole.Viewer}
                  onChange={event => handleOrgDefaultRoleChange(event.target.value)}
                >
                  {ORG_DEFAULT_ROLE_OPTIONS.map(role => (
                    <MenuItem key={role} value={role}>
                      {capitalCase(role)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Card>
          <Card className={classes.ssoCard}>
            <div className={classes.ssoContent}>
              <div className={classes.ssoTextContainer}>
                <Typography variant="subtitle1">Signing Reminders</Typography>
                <Typography color="textSecondary" variant="body2">
                  How many days between signing reminders Aline should send. Set to None to disable
                  reminders.
                </Typography>
              </div>
              <Autocomplete<SigningReminderOption, false, true>
                disableClearable
                className={classes.signingReminderSelect}
                disabled={!canUpdateOrg}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, selected) => option.value === selected.value}
                options={SIGNING_REMINDER_OPTIONS}
                renderInput={params => <AlineAutocompleteInput {...params} />}
                size="small"
                value={signingReminderValue}
                onChange={(_event, selected) => handleSigningReminderChange(selected)}
              />
            </div>
          </Card>
          {currentUser && (
            <ParentChildRelationshipsCard
              canUpdateOrg={canUpdateOrg}
              myRepositoryId={currentUser.myRepositoryId}
              parentChildEnabled={currentUser.org.parentChildEnabled}
            />
          )}
          {currentUser && (
            <SignatureDetectionCard
              backfillRequestedAt={currentUser.org.signatureDetectionBackfillRequestedAt ?? null}
              canUpdateOrg={canUpdateOrg}
              signatureDetectionEnabled={currentUser.org.signatureDetectionEnabled}
            />
          )}
          <Card className={classes.ssoCard}>
            <div className={classes.ssoContent}>
              <div className={classes.ssoTextContainer}>
                <Typography variant="subtitle1">Single Sign-On (SSO)</Typography>
                <Typography color="textSecondary" variant="body2">
                  Manage SSO authentication for your organization.
                </Typography>
              </div>
              <AlineButton
                color="primary"
                loading={ssoLoading}
                variant="outlined"
                onClick={handleManageSSOClick}
              >
                Manage SSO
              </AlineButton>
            </div>
          </Card>
          <Card className={classes.ssoCard}>
            <div className={classes.ssoContent}>
              <div className={classes.ssoTextContainer}>
                <Typography variant="subtitle1">Directory Sync</Typography>
                <Typography color="textSecondary" variant="body2">
                  Sync users from your corporate directory (e.g., Azure AD, Okta, etc.).
                </Typography>
              </div>
              <AlineButton
                color="primary"
                loading={dirSyncLoading}
                variant="outlined"
                onClick={handleManageDirectorySyncClick}
              >
                Manage Directory
              </AlineButton>
            </div>
          </Card>
          <Card className={classes.ssoCard}>
            <div className={classes.ssoContent}>
              <div className={classes.ssoTextContainer}>
                <Typography variant="subtitle1">Domain Verification</Typography>
                <Typography color="textSecondary" variant="body2">
                  {"Verify your organization's domain to ensure secure authentication."}
                </Typography>
              </div>
              <AlineButton
                color="primary"
                loading={domainVerificationLoading}
                variant="outlined"
                onClick={handleManageDomainVerificationClick}
              >
                Manage Domain Verification
              </AlineButton>
            </div>
          </Card>
        </div>
      </div>
      <UploadOrgAvatarPicker
        domContainer={FileStackDomContainer.OrgAvatar}
        open={openUploadAvatarPicker}
        pickerOpts={{
          onClose: () => setOpenUploadAvatarPicker(false),
        }}
      />
    </div>
  );
}

const DISABLE_MESSAGE =
  'Aline AI will stop identifying relationships for new documents. Relationships already found will remain visible. You can re-enable this at any time.';

const SIGNATURE_DETECTION_ENABLE_DIALOG = {
  message:
    'Aline AI will check new PDF uploads for signatures. When it finds one on a Draft document, ' +
    'it sets the status to Signed and notifies the owner. Documents already in your repository ' +
    'are only checked if you scan them.',
  primaryText: 'Enable',
  title: 'Enable Signature Detection?',
};

const SIGNATURE_DETECTION_DISABLE_DIALOG = {
  message:
    'New uploads will no longer be checked for signatures, and a scan in progress stops. ' +
    'Statuses Aline AI already set stay as they are.',
  primaryText: 'Disable',
  title: 'Disable Signature Detection?',
};

const SIGNATURE_DETECTION_BACKFILL_DIALOG_MESSAGE =
  'Aline AI will check every PDF already in your repository. Drafts with a signature are marked ' +
  "Signed, and you can change any of them back. Owners aren't notified. The scan runs in the " +
  'background and can take a while for a large repository.';

// Measured as the average AI token usage across ~180 live document analyses.
const ESTIMATED_TOKENS_PER_DOCUMENT = 150_000;

function getEnableMessage(unprocessedRelationshipDocumentsCount: number): string {
  if (unprocessedRelationshipDocumentsCount <= 0) {
    return "Aline AI will keep analyzing new documents as they're uploaded.";
  }

  const estimatedTokens = unprocessedRelationshipDocumentsCount * ESTIMATED_TOKENS_PER_DOCUMENT;
  return (
    `Aline AI will analyze your ${unprocessedRelationshipDocumentsCount} unprocessed documents. ` +
    `Estimated usage: ~${formatUsageNumber(estimatedTokens)} AI tokens. Actual usage varies with document length and complexity.`
  );
}

export function ParentChildRelationshipsCard({
  canUpdateOrg,
  myRepositoryId,
  parentChildEnabled,
}: {
  canUpdateOrg: boolean;
  myRepositoryId: UUID;
  parentChildEnabled: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [setParentChildRelationshipsEnabled, { loading }] =
    Apollo.useSetParentChildRelationshipsEnabledMutation();
  const {
    detectingRelationshipsCount,
    loading: metaLoading,
    unprocessedRelationshipDocumentsCount,
  } = useRepositoryMeta(myRepositoryId);
  const { currentUser, loading: currentUserLoading } = useCurrentUserQuery();
  const hasActiveSubscription =
    currentUser?.org.alineSubscription?.status === Apollo.SubscriptionStatus.Active;
  const isOutOfAiTokens = Boolean(
    currentUser && currentUser.aiUsage.usedTokens > currentUser.aiUsage.totalAvailableTokens
  );
  // The server turns the feature off when an org without a subscription runs out of tokens.
  const isPausedForAiTokens = !parentChildEnabled && !hasActiveSubscription && isOutOfAiTokens;
  const logAndDispatchErrorSnack = useLogAndDispatchErrorSnack({
    message: 'Could not update Parent-Child Relationships. Please try again.',
  });

  /**
   * Gated on the switch being on: after a mid-run disable the detecting count can
   * lag (or linger on stamps settled server-side), and a disabled card must never
   * claim to be analyzing.
   */
  const hasActiveRun = parentChildEnabled && detectingRelationshipsCount > 0;
  /**
   * Only turning the feature on is gated. Turning it off must always work,
   * since it may be stopping a run that is spending tokens. Enabling waits for:
   * - the repository counts, which pick the dialog's token estimate. An
   *   unloaded count reads as 0 and would hide the warning before a backfill.
   * - the current user's AI usage, and stays blocked while the org is over its
   *   limit, because the server rejects the enable.
   */
  const isSwitchDisabled =
    !canUpdateOrg ||
    loading ||
    (!parentChildEnabled && (metaLoading || currentUserLoading || isOutOfAiTokens));

  const dialogCopy = pendingEnabled
    ? {
        message: getEnableMessage(unprocessedRelationshipDocumentsCount),
        primaryText: 'Enable',
        title: 'Enable Parent-Child Relationships?',
      }
    : {
        message: hasActiveRun
          ? `An analysis is currently running and will be stopped. ${DISABLE_MESSAGE}`
          : DISABLE_MESSAGE,
        primaryText: 'Disable',
        title: 'Disable Parent-Child Relationships?',
      };

  const handleConfirm = useCallback(async () => {
    if (pendingEnabled === null) return;

    /**
     * Must never reject: ConfirmationDialog flips its submitting state around
     * this callback and only settles it when the promise resolves, so a
     * thrown failure (e.g. a network error, which Apollo throws instead of
     * returning) would leave the dialog stuck in its loading state.
     */
    try {
      const { error } = await setParentChildRelationshipsEnabled({
        variables: { enabled: pendingEnabled },
      });
      if (error) logAndDispatchErrorSnack(error);
    } catch (error) {
      const fallbackError =
        error instanceof Error ? error : new Error('Something went wrong. Please try again.');
      logAndDispatchErrorSnack(fallbackError);
    }
  }, [logAndDispatchErrorSnack, pendingEnabled, setParentChildRelationshipsEnabled]);

  return (
    <>
      <Card className={classes.ssoCard}>
        <div className={classes.ssoContent}>
          <div className={classes.ssoTextContainer}>
            <div className={classes.titleRow}>
              {hasActiveRun && (
                <span className={classes.titlePulse}>
                  <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
                </span>
              )}
              <Typography variant="subtitle1">
                {hasActiveRun
                  ? 'Detecting parent-child relationships…'
                  : 'Parent-Child Relationships'}
              </Typography>
            </div>
            <Typography color="textSecondary" variant="body2">
              Aline AI automatically identifies relationships across all your documents.
            </Typography>
          </div>
          <div className={classes.toggleContainer}>
            <ToggleSwitch
              disabled={isSwitchDisabled}
              toggle={() => setPendingEnabled(!parentChildEnabled)}
              toggled={parentChildEnabled}
            />
          </div>
        </div>
        {canUpdateOrg && isPausedForAiTokens && (
          <div className={classes.statusRow}>
            <Typography color="textSecondary" variant="body2">
              Paused: your AI usage limit was reached.
            </Typography>
          </div>
        )}
      </Card>
      <ConfirmationDialog
        message={dialogCopy.message}
        open={pendingEnabled !== null}
        primaryText={dialogCopy.primaryText}
        secondaryText="Cancel"
        title={dialogCopy.title}
        onClickPrimary={handleConfirm}
        onClose={() => setPendingEnabled(null)}
      />
    </>
  );
}

/**
 * Scans the PDFs already in the repository, an opt-in apart from the on/off
 * switch since it can set many existing documents to Signed.
 */
const SignatureDetectionBackfillPanel = React.memo(function SignatureDetectionBackfillPanel({
  backfillRequestedAt,
  canUpdateOrg,
}: {
  backfillRequestedAt: string | null;
  canUpdateOrg: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [startBackfill, { loading }] = Apollo.useStartSignatureDetectionBackfillMutation();
  const dispatchStartedSnack = useDispatchSuccessSnack({
    message: 'Scan started. Statuses update as each document is checked.',
  });
  const logAndDispatchErrorSnack = useLogAndDispatchErrorSnack({
    message: 'Could not start the scan. Please try again.',
  });

  const handleConfirm = useCallback(async () => {
    // Must never reject; see ParentChildRelationshipsCard.
    try {
      const { error } = await startBackfill();
      if (error) logAndDispatchErrorSnack(error);
      else dispatchStartedSnack();
    } catch (error) {
      const fallbackError =
        error instanceof Error ? error : new Error('Something went wrong. Please try again.');
      logAndDispatchErrorSnack(fallbackError);
    }
  }, [dispatchStartedSnack, logAndDispatchErrorSnack, startBackfill]);

  const openConfirm = useCallback(() => setIsConfirmOpen(true), []);
  const closeConfirm = useCallback(() => setIsConfirmOpen(false), []);

  return (
    <>
      <div className={classes.signatureDetectionBackfillPanel}>
        <div className={classes.ssoTextContainer}>
          <Typography variant="subtitle2">Check documents you already have</Typography>
          <Typography color="textSecondary" variant="body2">
            New uploads are checked automatically. Existing PDFs are only checked if you scan them.
          </Typography>
          {Boolean(backfillRequestedAt) && (
            <Typography color="textSecondary" variant="caption">
              Last scan started {formatDateForDisplay(backfillRequestedAt)}
            </Typography>
          )}
        </div>
        <AlineButton
          className={classes.signatureDetectionBackfillButton}
          color="primary"
          disabled={!canUpdateOrg}
          loading={loading}
          variant="outlined"
          onClick={openConfirm}
        >
          Scan PDFs
        </AlineButton>
      </div>
      <ConfirmationDialog
        message={SIGNATURE_DETECTION_BACKFILL_DIALOG_MESSAGE}
        open={isConfirmOpen}
        primaryText="Start scan"
        secondaryText="Cancel"
        title="Scan existing PDFs?"
        onClickPrimary={handleConfirm}
        onClose={closeConfirm}
      />
    </>
  );
});

/**
 * The signature detection beta. The switch covers new uploads: disabling
 * stops new scans and keeps statuses already set. Existing PDFs are scanned
 * only through the scan panel under the switch.
 */
export function SignatureDetectionCard({
  backfillRequestedAt,
  canUpdateOrg,
  signatureDetectionEnabled,
}: {
  backfillRequestedAt: string | null;
  canUpdateOrg: boolean;
  signatureDetectionEnabled: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const [setSignatureDetectionEnabled, { loading }] =
    Apollo.useSetSignatureDetectionEnabledMutation();
  const logAndDispatchErrorSnack = useLogAndDispatchErrorSnack({
    message: 'Could not update Signature Detection. Please try again.',
  });

  const dialogCopy = pendingEnabled
    ? SIGNATURE_DETECTION_ENABLE_DIALOG
    : SIGNATURE_DETECTION_DISABLE_DIALOG;

  const handleConfirm = useCallback(async () => {
    if (pendingEnabled === null) return;

    // Must never reject; see ParentChildRelationshipsCard.
    try {
      const { error } = await setSignatureDetectionEnabled({
        variables: { enabled: pendingEnabled },
      });
      if (error) logAndDispatchErrorSnack(error);
    } catch (error) {
      const fallbackError =
        error instanceof Error ? error : new Error('Something went wrong. Please try again.');
      logAndDispatchErrorSnack(fallbackError);
    }
  }, [logAndDispatchErrorSnack, pendingEnabled, setSignatureDetectionEnabled]);

  return (
    <>
      <Card className={classes.ssoCard}>
        <div className={classes.ssoContent}>
          <div className={classes.ssoTextContainer}>
            <div className={classes.titleRow}>
              <Typography variant="subtitle1">Signature Detection</Typography>
              <Chip
                className={classes.betaChip}
                color="primary"
                label="Beta"
                size="small"
                variant="outlined"
              />
            </div>
            <Typography color="textSecondary" variant="body2">
              Aline AI checks each PDF you upload for signatures and marks signed Drafts as Signed.
            </Typography>
          </div>
          <div className={classes.toggleContainer}>
            <ToggleSwitch
              disabled={!canUpdateOrg || loading}
              toggle={() => setPendingEnabled(!signatureDetectionEnabled)}
              toggled={signatureDetectionEnabled}
            />
          </div>
        </div>
        {signatureDetectionEnabled && (
          <SignatureDetectionBackfillPanel
            backfillRequestedAt={backfillRequestedAt}
            canUpdateOrg={canUpdateOrg}
          />
        )}
      </Card>
      <ConfirmationDialog
        message={dialogCopy.message}
        open={pendingEnabled !== null}
        primaryText={dialogCopy.primaryText}
        secondaryText="Cancel"
        title={dialogCopy.title}
        onClickPrimary={handleConfirm}
        onClose={() => setPendingEnabled(null)}
      />
    </>
  );
}

export function useOrgSchema() {
  const client = useApolloClient();
  const currentUser = useSelectCurrentUser();
  const OrgSchema = Yup.object<EditOrgValues>({
    memberEmails: Yup.array().of(Yup.string().email('Must enter valid email')),
    orgName: Yup.string()
      .required('Required')
      .test('server-validation', 'Org name must be unique', async name => {
        if (!name || currentUser?.org?.name === name) return true;

        const { data } = await client.query<
          Apollo.OrgNameUniqueQuery,
          Apollo.OrgNameUniqueQueryVariables
        >({ query: Apollo.OrgNameUniqueDocument, variables: { input: { name } } });

        return !!data?.orgNameUnique.unique;
      }),
  });

  return { OrgSchema };
}

function useManageSSO() {
  const currentUser = useSelectCurrentUser();
  const [loading, setLoading] = useState<boolean>(false);
  const dispatchNoOrgNotification = useDispatchNotification({
    message: 'Please create an organization before configuring SSO.',
    variant: 'error',
  });
  const handleSSOClick = useCallback(async () => {
    if (!currentUser?.org) return dispatchNoOrgNotification();

    setLoading(true);
    const res = await postFetch<ConfigureSSOBody>(routes.ApiSSO, {
      alineOrgId: currentUser.org.id,
    });
    const { redirectUri } = RedirectURIOptionalSchema.parse(await res.json());
    if (redirectUri?.route) window.location.href = redirectUri.route;
    setLoading(false);
  }, [currentUser, dispatchNoOrgNotification]);

  return { currentUser, handleSSOClick, loading };
}

function useManageDirectorySync() {
  const currentUser = useSelectCurrentUser();
  const [loading, setLoading] = useState<boolean>(false);
  const dispatchNoOrgNotification = useDispatchNotification({
    message: 'Please create an organization before configuring Directory Sync.',
    variant: 'error',
  });

  const handleDirectorySyncClick = useCallback(async () => {
    if (!currentUser?.org) return dispatchNoOrgNotification();

    setLoading(true);
    const res = await postFetch<ConfigureSSOBody>(routes.ApiEnableDirectorySync, {
      alineOrgId: currentUser.org.id,
    });
    const { redirectUri } = RedirectURIOptionalSchema.parse(await res.json());
    if (redirectUri?.route) window.location.href = redirectUri.route;
    setLoading(false);
  }, [currentUser, dispatchNoOrgNotification]);

  return { currentUser, handleDirectorySyncClick, loading };
}

function useManageDomainVerification() {
  const currentUser = useSelectCurrentUser();
  const [loading, setLoading] = useState<boolean>(false);
  const dispatchNoOrgNotification = useDispatchNotification({
    message: 'Please create an organization before configuring Domain Verification.',
    variant: 'error',
  });

  const handleDomainVerificationClick = useCallback(async () => {
    if (!currentUser?.org) return dispatchNoOrgNotification();

    setLoading(true);
    const res = await postFetch<EnableDomainVerificationBody>(routes.ApiEnableDomainVerification, {
      alineOrgId: currentUser.org.id,
    });
    const { redirectUri } = RedirectURIOptionalSchema.parse(await res.json());
    if (redirectUri?.route) window.location.href = redirectUri.route;
    setLoading(false);
  }, [currentUser, dispatchNoOrgNotification]);

  return { currentUser, handleDomainVerificationClick, loading };
}
